import { Component, EventEmitter, Input, OnInit, Output, NgZone } from '@angular/core';
import { SessionStorageService } from 'src/app/services/session-storage.service';

declare var SerasaExperianIDF: any;

const SERASA_UNIFICADO_LIVENESS_CONTADOR = 60;

@Component({
  selector: 'app-image-capture-serasa-unificado-liveness',
  templateUrl: './image-capture-serasa-unificado-liveness.component.html',
  styleUrls: ['./image-capture-serasa-unificado-liveness.component.scss']
})
export class ImageCaptureSerasaUnificadoLivenessComponent implements OnInit {
  @Output()
  imageCapture = new EventEmitter<any>();

  @Output()
  captureError = new EventEmitter<string>();

  @Output()
  isLoading = new EventEmitter<boolean>();

  @Input()
  isSelf: boolean = false;

  constructor(
    private sessionStorageService: SessionStorageService,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.initSDK();
  }

  private initSDK(): void {
    const config = this.sessionStorageService.getEnvConfig()['serasa-unificado-liveness'];

    if (!config || !config.apiKey || !config.sdkUrl) {
      this.captureError.emit("Configuração do Serasa Unificado Liveness não encontrada.");
      return;
    }

    this.isLoading.emit(true);

    if (typeof SerasaExperianIDF !== "undefined") {
      this.setupSDK(config.apiKey);
    } else {
      this.loadScriptAndSetup(config.sdkUrl, config.apiKey);
    }
  }

  private loadScriptAndSetup(url: string, apiKey: string): void {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.defer = true;

    (window as any).onSerasaExperianIDFReady = () => {
      this.ngZone.run(() => {
        this.setupSDK(apiKey);
      });
    };

    script.onerror = () => {
      this.ngZone.run(() => {
        this.captureError.emit("Erro ao carregar o SDK do Serasa Unificado Liveness.");
        this.isLoading.emit(false);
      });
    };

    document.head.appendChild(script);
  }

  private setupSDK(apiKey: string): void {
    SerasaExperianIDF.setup({ apiKey })
      .then(() => {
        this.setupLiveness();
      })
      .catch((error: any) => {
        console.error("Erro no setup do Serasa SDK:", error);
        this.captureError.emit("Erro ao inicializar o SDK do Serasa.");
        this.isLoading.emit(false);
      });
  }

  private resolverContadorEmSegundos(valor: unknown): number {
    const segundos = Number(valor);
    return Number.isInteger(segundos) && segundos > 0
      ? segundos
      : SERASA_UNIFICADO_LIVENESS_CONTADOR;
  }

  private setupLiveness(): void {
    const config = this.sessionStorageService.getEnvConfig()['serasa-unificado-liveness'];
    const countdownToRun = this.resolverContadorEmSegundos(config?.countdownToRun);
    const customOptions = {
      liveness: {
        provider: "serasa",
        serasaCustomization: {
          countdownToRun,
        },
      },
    };

    SerasaExperianIDF.setupLiveness({
      options: customOptions,
      callback: (res: any) => {
        console.log("SerasaExperianIDF.setupLiveness - callback:", res);
      },
    })
      .then(() => {
        this.isLoading.emit(false);
        this.startCapture();
      })
      .catch((error: any) => {
        console.error("Erro no setupLiveness:", error);
        this.captureError.emit("Erro ao configurar o Liveness.");
        this.isLoading.emit(false);
      });
  }

  private startCapture(): void {
    SerasaExperianIDF.startLiveness()
      .then((livenessResult: any) => {
        this.ngZone.run(() => {
          this.processResult(livenessResult);
        });
      })
      .catch((error: any) => {
        this.ngZone.run(() => {
          console.error('Erro no startLiveness:', error);
          const errorMsg = this.getErrorMessage(error);
          this.captureError.emit(errorMsg);
        });
      });
  }

  private readBlobAsBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadstart = () => console.log("Serasa Unificado Liveness - Reading image blob...");
      reader.onloadend = () => {
        const base64data = reader.result as string;
        const base64 = base64data.split(",")[1];
        resolve(base64);
      };
      reader.onerror = (err) => {
        console.error("Serasa Unificado Liveness - Error reading blob:", err);
        reject(err);
      };
      reader.readAsDataURL(blob);
    });
  }

  private async processResult(result: any): Promise<void> {
    console.log('Serasa Unificado Liveness - result:', result);

    if (result && result.images && result.images.length > 0) {
      console.log(`Serasa Unificado Liveness - image count: ${result.images.length}`);
      console.log(`Serasa Unificado Liveness - event (fingerprint): ${result.event}`);

      try {
        const image1 = await this.readBlobAsBase64(result.images[0]);

        console.log("Serasa Unificado Liveness - images converted to base64 successfully.");

        // Emit result. event is the fingerprint required by backend
        this.imageCapture.emit({
          image: image1,
          appKey: result.event,
          jwt: result.event,
        });
      } catch (err) {
        this.captureError.emit("Erro ao converter imagens capturadas.");
      }
    } else {
      console.error("Serasa Unificado Liveness - No images captured in result.");
      this.captureError.emit("Nenhuma imagem capturada.");
    }
  }

  private getErrorMessage(error: any): string {
    if (error.code === 300 && error.status === 7) return 'Captura cancelada pelo usuário.';
    if (error.code === 300 && error.status === 12) return 'Câmera não habilitada.';
    if (error.code === 200 && error.status === 5) return 'Dispositivo não suportado.';

    return error.message || 'Erro inesperado durante a captura facial.';
  }
}
